import React, { Component } from 'react';

import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import { navigate, RouteComponentProps } from '@reach/router';

import { Consumer } from '../../../../store';
import { IAuthActions } from '../../../../store/auth/actions';
import { IAuthStore } from '../../../../store/auth/store';
import { IInvitationActions } from '../../../../store/player/invitation/actions';
import { IInvitationStore } from '../../../../store/player/invitation/store';

import { redWaglLogo } from '../../../../assets/images/';
import Button from '../../../../components/Button';
import { FormConsumer, FormProvider, IFormAttributes } from '../../../../components/Forms';
import Input from '../../../../components/Input';
import { externalRoutes } from '../../../../routes/Utils/Routes/external.routes';

interface IState {
  invalidEmail: boolean;
  invalidEmailMessage: string;
  invalidPassword: boolean;
  invalidPasswordMessage: string;
}

interface IProps extends RouteComponentProps {
  actions: { auth: IAuthActions; invitation?: IInvitationActions };
  invitationId?: string;
}

class Account extends Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);
    this.state = {
      invalidEmail: false,
      invalidEmailMessage: '',
      invalidPassword: false,
      invalidPasswordMessage: ''
    };

    this.onSubmit = this.onSubmit.bind(this);
  }

  public async componentDidMount(): Promise<void> {
    const { invitationId: code } = this.props;
    if (code && this.props.actions.invitation) {
      const response = await this.props.actions.invitation.get(code);
      if (!response.invitation) {
        navigate(`/`);
      }
    }
  }

  public onSubmit(invitation: IInvitationStore): (data: IAuthStore) => Promise<void> {
    return async (data: IAuthStore) => {
      const response = await this.props.actions.auth.register(data);
      if (response.errors && response.errors.auth && response.errors.auth.el === 'password1') {
        this.setState({
          invalidPassword: true,
          invalidPasswordMessage: response.errors.auth.msg || 'Error'
        });
      }
      if (response.errors && response.errors.auth && response.errors.auth.el === 'email') {
        this.setState({ invalidEmail: true, invalidEmailMessage: response.errors.auth.msg || '' });
      }
      if (response.auth && response.auth.isAuthenticated) {
        navigate(invitation ? `/register/golf-info/${invitation.club.id}` : '/');
      }
    };
  }

  public render(): JSX.Element {
    return (
      <Consumer>
        {({ auth, invitation }) => (
          <div>
            {(!this.props.invitationId || (this.props.invitationId && invitation.email)) && (
              <FormProvider
                defaultValues={{ ...auth, user: { ...auth.user, email: invitation.email } }}
                onSubmit={this.onSubmit(invitation)}
                validations={{
                  acceptedToS: 'isTrue',
                  password1: 'password',
                  password2: values => values.password1 === values.password2,
                  'user.email': 'required',
                  'user.firstName': 'required',
                  'user.lastName': 'required'
                }}
              >
                <FormConsumer>
                  {({
                    values,
                    formActions: { setValue, toggleValue },
                    errors
                  }: IFormAttributes) => (
                    <div className="account form">
                      <div className="logo-wrapper">
                        <img src={redWaglLogo} className="logo" alt="logo" />
                      </div>

                      <h1>ACCOUNT SETUP</h1>

                      <Input
                        label="FIRST NAME"
                        id="first-name"
                        type="text"
                        value={values.user.firstName}
                        onChange={setValue('user.firstName')}
                        hasError={errors['user.firstName']}
                        errorMessage="First Name cannot be empty"
                        fullWidth={true}
                      />

                      <Input
                        label="LAST NAME"
                        id="last-name"
                        type="text"
                        value={values.user.lastName}
                        onChange={setValue('user.lastName')}
                        hasError={errors['user.lastName']}
                        errorMessage="Last Name cannot be empty"
                        fullWidth={true}
                      />

                      <Input
                        label="EMAIL"
                        id="email"
                        type="email"
                        value={values.user.email}
                        validated={!!this.props.invitationId}
                        disabled={!!invitation.email}
                        onChange={setValue('user.email')}
                        hasError={errors['user.email'] || this.state.invalidEmail}
                        errorMessage={
                          this.state.invalidEmailMessage || 'You must provide a valid email'
                        }
                        fullWidth={true}
                      />

                      <Input
                        label="PASSWORD"
                        id="password1"
                        type="password"
                        onChange={setValue('password1')}
                        hasError={errors.password1 || this.state.invalidPassword}
                        errorMessage={this.state.invalidPasswordMessage}
                        fullWidth={true}
                      />

                      <Input
                        label="CONFIRM PASSWORD"
                        id="password2"
                        type="password"
                        onChange={setValue('password2')}
                        hasError={errors.password2}
                        errorMessage="Passwords must match"
                        fullWidth={true}
                      />

                      <p className="tos-checkbox-cta">
                        BEFORE REGISTERING PLEASE READ THE&nbsp;
                        <a href={externalRoutes.termsOfService} target="_blank">
                          TERMS & CONDITIONS
                        </a>{' '}
                        FOR USING WAGL:
                      </p>
                      <FormControlLabel
                        classes={{ label: 'tos-checkbox-label-player override' }}
                        control={
                          <Checkbox
                            checkedIcon={<CheckBoxIcon className="form-icon override" />}
                            color="primary"
                            icon={<CheckBoxOutlineBlankIcon className="form-icon override" />}
                            id="accepted-tos"
                            onChange={toggleValue('acceptedToS')}
                            value="on"
                          />
                        }
                        label={
                          <>
                            By creating an account I acknowledge I’ve read and I agree to
                            WAGL’s&nbsp;
                            <a href={externalRoutes.termsOfService} target="_blank">
                              Terms of Service
                            </a>{' '}
                            and&nbsp;
                            <a href={externalRoutes.privacyPolicy} target="_blank">
                              Privacy Policy
                            </a>
                            .
                          </>
                        }
                      />

                      <Button
                        className="register-next-button button--shadow"
                        color="primary"
                        disabled={errors.FORM}
                        type="submit"
                        variant="contained"
                      >
                        NEXT
                      </Button>
                    </div>
                  )}
                </FormConsumer>
              </FormProvider>
            )}
          </div>
        )}
      </Consumer>
    );
  }
}

export { Account };
